import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Loader2, Download, UserPlus, Shuffle, UserMinus, ExternalLink, History } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { API_URL, SLA, STATUS_LABEL, Clock, SlaBadge, fmtDT, downloadExport } from './lmShared';
import { LmAssignDialog } from './LmAssignDialog';
import { LmHistoryDialog } from './LmHistoryDialog';

const ACT = 'text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:border-[#2D3E6F] hover:text-[#2D3E6F] flex items-center gap-1 disabled:opacity-40 disabled:pointer-events-none';
const CHIPS = ['', 'expirada', 'a_expirar', 'parada', 'atrasada', 'em_curso', 'aguarda_contacto', 'por_atribuir', 'convertida', 'fechada_sem_contrato'];

export const LmLeadsSheet = ({ open, title, filters, initialSla, users, onClose, onChanged }) => {
  const [sla, setSla] = useState(initialSla || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState([]);
  const [assignMode, setAssignMode] = useState(null);
  const [histId, setHistId] = useState(null);
  const [soNaoVistas, setSoNaoVistas] = useState(false);

  useEffect(() => { setSla(initialSla || ''); }, [initialSla, open]);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/lead-mgmt/leads`, { params: { ...filters, sla: sla || undefined } });
      setItems(r.data.items || []);
      setSel([]);
    } finally {
      setLoading(false);
    }
  }, [open, filters, sla]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const selectable = items.filter((l) => l.status !== 'convertida');
  const allSel = selectable.length > 0 && sel.length === selectable.length;

  const unassign = async () => {
    try {
      const r = await axios.post(`${API_URL}/leads/bulk-assign`, { lead_ids: sel, mode: 'unassign' });
      toast.success(r.data.message);
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const done = () => { setAssignMode(null); load(); onChanged(); };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto" data-testid="lm-leads-sheet">
        <SheetHeader><SheetTitle data-testid="lm-sheet-title">{title}{sla ? ` · ${sla === 'atrasada' ? 'Follow-up em atraso' : SLA[sla].label}` : ''}</SheetTitle></SheetHeader>
        <div className="flex flex-wrap gap-1.5 my-3">
          <button type="button" onClick={() => setSoNaoVistas((v) => !v)} data-testid="lm-chip-nao-vistas"
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${soNaoVistas ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-rose-200 text-rose-700 hover:border-rose-500'}`}>
            Só não vistas ({items.filter((l) => l.vista_status === 'nao_vista').length})
          </button>
          {CHIPS.map((c) => (
            <button key={c || 'all'} type="button" onClick={() => setSla(c)} data-testid={`lm-chip-${c || 'todas'}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${sla === c ? 'bg-[#2D3E6F] text-white border-[#2D3E6F]' : 'bg-white border-slate-200 text-slate-600 hover:border-[#2D3E6F]'}`}>
              {c === '' ? 'Todas' : c === 'atrasada' ? 'Follow-up em atraso' : SLA[c].label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-2 sticky top-0 bg-white py-2 z-10 border-b">
          <span className="text-xs text-slate-500" data-testid="lm-selected-count">{sel.length} selecionada(s) · {items.length} lead(s)</span>
          <button disabled={!sel.length} onClick={() => setAssignMode('single')} className={ACT} data-testid="lm-act-assign"><UserPlus className="w-3.5 h-3.5" /> Atribuir a…</button>
          <button disabled={!sel.length} onClick={() => setAssignMode('round_robin')} className={ACT} data-testid="lm-act-distribute"><Shuffle className="w-3.5 h-3.5" /> Distribuir por vários</button>
          <button disabled={!sel.length} onClick={unassign} className={ACT} data-testid="lm-act-unassign"><UserMinus className="w-3.5 h-3.5" /> Retirar atribuição</button>
          <button onClick={() => downloadExport(axios, { kind: 'leads', ...filters, sla: sla || undefined })} className={`${ACT} ml-auto`} data-testid="lm-act-export"><Download className="w-3.5 h-3.5" /> Exportar Excel</button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-400 border-b">
                  <th className="py-2 pr-2"><input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : selectable.map((l) => l.id))} data-testid="lm-select-all" /></th>
                  <th className="pr-3">Cliente</th><th className="pr-3">Relógio</th><th className="pr-3">Estado</th>
                  <th className="pr-3">Comercial</th><th className="pr-3">Atribuída</th><th className="pr-3">Último contacto</th>
                  <th className="pr-3">Próximo</th><th />
                </tr>
              </thead>
              <tbody>
                {(soNaoVistas ? items.filter((l) => l.vista_status === 'nao_vista') : items).map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`lm-lead-row-${l.id}`}>
                    <td className="py-2 pr-2">{l.status !== 'convertida' && <input type="checkbox" checked={sel.includes(l.id)} onChange={() => toggle(l.id)} data-testid={`lm-select-${l.id}`} />}</td>
                    <td className="pr-3"><p className="font-medium text-slate-800">{l.client_name}</p><p className="text-[11px] text-slate-400">{l.client_phone || ''} {l.service_interest ? `· ${l.service_interest}` : ''}</p></td>
                    <td className="pr-3 space-y-0.5"><SlaBadge sla={l.sla} /><div><Clock l={l} /></div></td>
                    <td className="pr-3 text-xs">{STATUS_LABEL[l.status] || l.status}{l.atrasada && <span className="block text-[10px] text-rose-600 font-semibold">Follow-up em atraso</span>}</td>
                    <td className="pr-3 text-xs">{l.assigned_to_name || '—'}
                      {l.vista_status === 'nao_vista' && <span className="block mt-0.5 w-fit text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-600 text-white" data-testid={`lm-nao-vista-${l.id}`}>Não vista</span>}
                      {l.vista_status === 'vista' && <span className="block mt-0.5 text-[10px] text-emerald-700" data-testid={`lm-vista-${l.id}`}>Vista {fmtDT(l.vista_at)}</span>}
                    </td>
                    <td className="pr-3 text-xs text-slate-500">{fmtDT(l.assigned_at || l.created_at)}</td>
                    <td className="pr-3 text-xs text-slate-500 max-w-[200px]"><p>{fmtDT(l.last_contact_at)}</p>{l.last_contact_note && <p className="truncate text-slate-400" title={l.last_contact_note}>{l.last_contact_note}</p>}</td>
                    <td className="pr-3 text-xs text-slate-500">{fmtDT(l.next_action_at)}</td>
                    <td className="whitespace-nowrap">
                      <button type="button" onClick={() => setHistId(l.id)} className="p-1 text-slate-400 hover:text-[#2D3E6F]" title="Histórico" data-testid={`lm-history-${l.id}`}><History className="w-4 h-4" /></button>
                      <Link to={`/leads/${l.id}`} className="p-1 inline-block text-slate-400 hover:text-[#2D3E6F]" title="Abrir lead" data-testid={`lm-open-${l.id}`}><ExternalLink className="w-4 h-4" /></Link>
                    </td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan={9} className="py-10 text-center text-slate-400">Sem leads neste filtro</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <LmAssignDialog mode={assignMode} leadIds={sel} users={users} onClose={() => setAssignMode(null)} onDone={done} />
        <LmHistoryDialog leadId={histId} onClose={() => setHistId(null)} />
      </SheetContent>
    </Sheet>
  );
};
